/**
 * Client-side access helpers (mirror backend userAccess.js).
 * Role priority: see rolePriority.js
 */
import {
  getUserRoleLabel as getRoleLabelFromPriority,
  resolvePrimaryRole,
  resolveAssignedRoles,
  shouldApplyRoleRestriction,
  getDefaultDashboardPath,
  ROLE_CODES,
} from './rolePriority';

export function isTruthyFlag(val) {
  return val === true || val === 1 || val === '1';
}

export function isAdmin(user) {
  return !!(user && isTruthyFlag(user.isAdmin));
}

export function isDeliveryHead(user) {
  return !!(user && isTruthyFlag(user.isDeliveryHead));
}

/** Admin-equivalent UI and routes (v1: includes Delivery Head). */
export function hasAdminLikeAccess(user) {
  if (user && user.hasAdminLikeAccess != null) {
    return isTruthyFlag(user.hasAdminLikeAccess);
  }
  return isAdmin(user) || isDeliveryHead(user);
}

/** Label with optional working location in brackets, e.g. "Employee (Bhopal)". */
export function formatLabelWithLocation(label, location) {
  const text = (label || '').trim();
  const loc = (location || '').trim();
  if (!text) return loc ? `(${loc})` : '';
  return loc ? `${text} (${loc})` : text;
}

/** Delivery Head without full admin — cannot use Manage Config, Add Project, Add Admin, Add Client, Add Project Manager. */
export function isDhOnlyRestricted(user) {
  return isDeliveryHead(user) && !isAdmin(user);
}

/** Full admin only — master skill catalog (Manage Skills), not personal My Skill Matrix. */
export function canManageSkillCatalog(user) {
  return isAdmin(user);
}

/** Bench Management: Admin or Delivery Head. */
export function canAccessBenchManagement(user) {
  return hasAdminLikeAccess(user);
}

/** Bench Analysis: full Admin only. */
export function canAccessBenchAnalysis(user) {
  return isAdmin(user);
}

/**
 * Show "My Timesheet" in admin nav (Actions / mobile drawer) for non-PM users.
 * Pure admins (admin only, no PM, no DH) are excluded; DH and admin+DH keep prior behavior.
 */
export function shouldShowAdminPersonalTimesheetNav(user) {
  if (!user || user.isProjectManager) return false;
  if (isAdmin(user) && !isDeliveryHead(user)) return false;
  return hasAdminLikeAccess(user);
}

/** Profile / nav role label from primary role. */
export function getUserRoleLabel(user, options = {}) {
  const rmAccess =
    options.rmMenuAccess ?? user?.isReportingManager ?? user?.reportingManagerFeaturesEnabled;
  const ctx = {
    isReportingManager: rmAccess,
    hasCsgProjects: user?.hasCsgProjects,
  };

  // RM-only users must show Reporting Manager even when a stale roleLabel says Employee.
  if (rmAccess && !user?.isProjectManager && !hasAdminLikeAccess(user)) {
    return getRoleLabelFromPriority(user, ctx);
  }

  if (user?.roleLabel && !options.forceCompute) {
    return user.roleLabel;
  }
  return getRoleLabelFromPriority(user, ctx);
}

export {
  resolvePrimaryRole,
  resolveAssignedRoles,
  shouldApplyRoleRestriction,
  getDefaultDashboardPath,
  ROLE_CODES,
};
